import { StrictTupleKey } from 'swr/_internal';
import useSWRImmutable from 'swr/immutable';
import config from '../../config';
import type { Campaign, ProductBundle, ProductWithBundleId, SelectableItem } from '../../models/CampaignModel';
import { Channel } from '~/models/ChannelModel';

export const getCampaign = async (partnerAlias: string, dwellingUnit: string, campaignUrl: string): Promise<Campaign> => {
  return fetch(`${config.apiUrl}/campaign/${partnerAlias}/${dwellingUnit}/${campaignUrl}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to fetch campaign: ${response.status} ${response.statusText}`);
    }
    return response.json();
  });
};

export const useCampaign = (partnerAlias: string, dwellingUnit: string, campaignUrl: string) => {
  let cacheKey: StrictTupleKey = ['get-campaign', partnerAlias, dwellingUnit, campaignUrl];
  if (!campaignUrl || !partnerAlias || !dwellingUnit) {
    cacheKey = null;
  }
  return useSWRImmutable(cacheKey, async () => {
    return await getCampaign(partnerAlias, dwellingUnit, campaignUrl);
  });
};

export const mapCampaignData = (data?: Campaign<SelectableItem>) => {
  if (!data) return data;
  const _campaign: Campaign<ProductWithBundleId> = {
    ...data,
    products: {
      subscription: mapProductGroup(data.products.subscription),
      premium: mapProductGroup(data.products.premium),
      receiver: mapProductGroup(data.products.receiver),
      installation: mapProductGroup(data.products.installation),
      equipment: mapProductGroup(data.products.equipment),
      shipment: mapProductGroup(data.products.shipment),
    },
  };
  return _campaign;
};

/**
 * Adds bundleId to each SelectableItem in each bundle
 */
function mapProductGroup(group: ProductBundle[]): ProductBundle<ProductWithBundleId>[] {
  return group.map((bundle) => {
    return {
      ...bundle,
      offers: bundle.offers.map((b) => ({ ...b, bundleId: bundle.id })),
    };
  });
}

const getActiveChannels = async (commercialProductId: number): Promise<Channel[]> => {
  return fetch(`${config.apiUrl}/channels/${commercialProductId}/active`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to fetch active channels: ${response.status} ${response.statusText}`);
    }
    return response.json();
  });
};

export const useActiveChannels = (commercialProductId: number, isAlacarte?: boolean) => {
  let cacheKey: StrictTupleKey = ['get-active-channels', commercialProductId, isAlacarte];
  if (!commercialProductId) {
    cacheKey = null;
  }
  return useSWRImmutable(cacheKey, async () => {
    const channels = await getActiveChannels(commercialProductId);
    if (isAlacarte === undefined) return channels;
    return channels.filter((channel) => channel.isAlaCarte === isAlacarte);
  });
};
